
import { defineComponent, onMounted, ref, SetupContext, computed } from "vue";
import NativeClient from "@/client/native-client";
import * as PIXI from "pixi.js-legacy";
import {
  CameraImageMap,
  RecognitionCandidate,
  RecognitionCandidateInfo,
  RecognitionCandidateMap,
  RequstCameraImage
} from "@/types/pantry";
import { convertRemToPx } from "@/utils/common-utils";
import store from "@/store";
import { LoadPantryCameraImage, PantryCameraImage } from "@/types/common";
export default defineComponent({
  name: "PantryCameraCheckImageView",
  props: {
    data: {
      type: RecognitionCandidate,
      default: null,
      required: true
    },
    selectPosition: {
      type: Number,
      default: 0,
      required: true
    }
  },
  setup(props, context: SetupContext) {
    const IMAGE_HEIGHT_REM = 24.9;
    const canvas = ref<HTMLCanvasElement>();
    const app = ref<PIXI.Application>();
    const width = ref(0);
    const height = ref(0);
    const isRequstDraw = ref(false);
    const isRequestLoad = ref(false);

    const createBlackOverlay = (
      containerToAdd: PIXI.Container,
      opacity: number,
      width: number,
      height: number
    ) => {
      const blackScreen = new PIXI.Graphics();
      blackScreen.moveTo(0, 0);
      blackScreen.beginFill(0x000000);
      blackScreen.drawRect(0, 0, width, height);
      blackScreen.endFill();
      blackScreen.filters = [new PIXI.filters.AlphaFilter(opacity)];
      containerToAdd.addChild(blackScreen);
      return blackScreen;
    };

    const createSpotlight = (
      targetRect: DOMRect,
      container: PIXI.Container,
      width: number,
      height: number
    ) => {
      NativeClient.sendDebugLog(
        "PantryCameraCheckImageView createSpotlight width=" +
          width +
          " height=" +
          height
      );
      const blackScreen = createBlackOverlay(container, 0.33, width, height);
      const clearCircle = new PIXI.Graphics();
      NativeClient.sendDebugLog(
        "PantryCameraCheckImageView createSpotlight targetRect=" +
          JSON.stringify(targetRect)
      );
      clearCircle.beginFill();
      clearCircle.drawRect(
        targetRect.x,
        targetRect.y,
        targetRect.width,
        targetRect.height
      );
      clearCircle.endFill();
      clearCircle.blendMode = PIXI.BLEND_MODES.ERASE;
      blackScreen.addChild(clearCircle);
      return blackScreen;
    };

    const drawRect = (targetRect: DOMRect) => {
      const drawRect = new PIXI.Graphics();
      drawRect.lineStyle(1, 0xffffff);
      drawRect.drawRect(
        targetRect.x,
        targetRect.y,
        targetRect.width,
        targetRect.height
      );
      drawRect.endFill();
      return drawRect;
    };
    const drawImage = (
      base64Data: string,
      width: number,
      height: number,
      info: RecognitionCandidateInfo
    ) => {
      if (canvas.value == undefined) {
        return;
      }
      if (isRequstDraw.value) {
        return;
      }
      NativeClient.sendDebugLog("PantryCameraCheckImageView drawImage");
      const image = new Image();
      image.src = "data:image/jpeg;base64," + base64Data;
      image.decoding = "async";
      image.onload = function() {
        store.dispatch("common/stopLoading");
        if (canvas.value == undefined) {
          return;
        }
        const texture = new PIXI.Texture(new PIXI.BaseTexture(image));
        NativeClient.sendDebugLog(
          "PantryCameraCheckImageView drawImage loaded texture.width=" +
            texture.width +
            " texture.height=" +
            texture.height
        );
        NativeClient.sendDebugLog(
          "PantryCameraCheckImageView drawImage loaded width=" +
            width +
            " height=" +
            height
        );
        NativeClient.sendDebugLog(
          "PantryCameraCheckImageView drawImage loaded image.width=" +
            image.width +
            " image.height=" +
            image.height
        );
        const viewWidth = width;
        const viewHeight = height;
        const app = new PIXI.Application({
          width: viewWidth,
          height: viewHeight,
          backgroundColor: 0xffffff,
          antialias: true,
          view: canvas.value
        });
        let scaleRate = viewWidth / texture.width;
        let scaleWidth = viewWidth;
        let scaleHeight = texture.height * scaleRate;
        if (scaleHeight > viewHeight) {
          scaleRate = viewHeight / texture.height;
          scaleWidth = texture.width * scaleRate;
          scaleHeight = viewHeight;
        }
        const textureImage = new PIXI.Sprite(texture);
        textureImage.width = scaleWidth;
        textureImage.height = scaleHeight;
        app.stage.addChild(textureImage);
        const spotlightState = new PIXI.Container();
        const left = Math.floor(scaleWidth * info.leftTopX);
        const top = Math.floor(scaleHeight * info.leftTopY);
        const right = Math.floor(scaleWidth * info.rightBottomX);
        const bottom = Math.floor(scaleHeight * info.rightBottomY);
        // NativeClient.sendDebugLog("PantryCameraCheckImageView drawImage loaded left=" + left + " top=" + top + " right=" + right + " bottom=" + bottom);
        const rect = new DOMRect(left, top, right - left, bottom - top);
        // NativeClient.sendDebugLog("PantryCameraCheckImageView drawImage loaded info=" + JSON.stringify(info));
        NativeClient.sendDebugLog(
          "PantryCameraCheckImageView drawImage loaded scaleWidth=" +
            scaleWidth +
            " scaleHeight=" +
            scaleHeight
        );
        // NativeClient.sendDebugLog("PantryCameraCheckImageView drawImage loaded rect=" + JSON.stringify(rect));
        createSpotlight(rect, spotlightState, viewWidth, viewHeight);
        app.stage.addChild(spotlightState);
        const drawContainer = new PIXI.Container();
        const draw = drawRect(rect);
        drawContainer.addChild(draw);
        app.stage.addChild(drawContainer);
      };
      isRequstDraw.value = true;
    };

    const setupDraw = () => {
      if (canvas.value == undefined) {
        return;
      }
      width.value = window.innerWidth;
      height.value = convertRemToPx(IMAGE_HEIGHT_REM);
    };

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(() => {
      NativeClient.sendDebugLog("PantryCameraCheckImageView onMounted");
      setupDraw();
    });

    const setDrawImage = (data: RecognitionCandidate, imageData: string | null) => {
      let result = false;
      const map: CameraImageMap = store.getters["pantry/getCameraImageMap"];
      if (map && data.key) {
        NativeClient.sendDebugLog("PantryCameraCheckImageView setDrawImage getCameraImageMap success");
        if (imageData == null) {
          const item = map.map.get(data.key);
          NativeClient.sendDebugLog("PantryCameraCheckImageView setDrawImage getCameraImageMap key=" + data.key);
          if (item && item.imageData) {
            imageData = item.imageData;
            const requst = new PantryCameraImage(data.key, imageData);
            NativeClient.savePantryCameraImage(requst);
          } else {
            NativeClient.sendDebugLog(
              "PantryCameraCheckImageView setDrawImage no data item=" +
                JSON.stringify(item)
            );
          }
        }
        if (props.data.info && imageData) {
          context.emit("image-file-name", data.key);
          drawImage(imageData, width.value, height.value, props.data.info);
          result = true;
        }
      }
      return result;
    };

    const requestLoadImage = async(data: RecognitionCandidate) => {
      NativeClient.sendDebugLog("PantryCameraCheckImageView requestLoadImage key=" + data.key);
      const result: RecognitionCandidateMap = store.getters["pantry/getRecognitionCandidateMap"];
      if (result && data.inferenceResultFoodId && data.imageUrlFileName && data.applianceId) {
        const item = result.map.get(data.inferenceResultFoodId);
        if (item && item.info) {
          const imageUrl = item.info.imageUrl;
          const payload = new RequstCameraImage(imageUrl, data.imageUrlFileName, data.applianceId);
          await Promise.all([store.dispatch("pantry/getCameraImageLoad", payload)]).then(result => {
            const draw = setDrawImage(props.data, null);
            NativeClient.sendDebugLog("PantryCameraCheckImageView requestLoadImage draw=" + draw + " key=" + props.data.key);
            if (!draw) {
              store.dispatch("common/stopLoading");
            }
          });
        } else {
          store.dispatch("common/stopLoading");
        }
      } else {
        store.dispatch("common/stopLoading");
      }
    };

    const requestLoadImageData = async (data: RecognitionCandidate) => {
      store.dispatch("common/startLoading");
      NativeClient.sendDebugLog("PantryCameraCheckImageView requestLoadImageData");
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        requestLoadImage(data);
      });
    };

    const drawScreenDrawImage = async (data: RecognitionCandidate) => {
      if (data.key) {
        NativeClient.sendDebugLog("PantryCameraCheckImageView drawScreenDrawImage getCameraImageMap success");
        const requst = new LoadPantryCameraImage(data.key);
        await Promise.all([NativeClient.loadPantryCameraImage(requst)]).then(result => {
          const imageResult = store.getters["common/getPantryCameraImage"];
          let draw = false;
          if (imageResult != null) {
            NativeClient.sendDebugLog("PantryCameraCheckImageView drawScreenDrawImage imageResult=" + JSON.stringify(imageResult));
            if (imageResult.imageData != "") {
              draw = setDrawImage(data, imageResult.imageData);
            }
          }
          if (!draw) {
            requestLoadImageData(props.data);
            isRequestLoad.value = true;
          }
        });
      } else {
        requestLoadImageData(props.data);
        isRequestLoad.value = true;
      }
    };
    const drawScreen = computed(async () => {
      // NativeClient.sendDebugLog("PantryCameraCheckImageView drawScreen" + JSON.stringify(props.data));
      if (canvas.value == undefined) {
        return;
      }
      NativeClient.sendDebugLog(
        "PantryCameraCheckImageView drawScreen id="
         + (props.data ? props.data.id : "empty")
         + " props.selectPosition="
         + props.selectPosition
         + " draw=" 
         + isRequstDraw.value
         + " load="
         + isRequestLoad.value);
      if (props.data && props.data.id == props.selectPosition && !isRequstDraw.value && !isRequestLoad.value) {
        drawScreenDrawImage(props.data);
      }
    });

    return {
      canvas,
      drawScreen
    };
  }
});
